import React, { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { dashboardApi } from '../../services/api';
import api from '../../services/api';
import AppShell from '../../components/layout/AppShell';
import { fmtPKR, fmtNum } from '../../utils/helpers';
import { Spinner } from '../../components/common';
import { RefreshCw, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const __COLORS = ['#1B2A4A','#F59E0B','#10B981','#EF4444','#3B82F6','#8B5CF6'];

export default function ManagementDashboard() {
  const [data, setData]       = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exec, appr] = await Promise.all([
        dashboardApi.executive().catch(() => ({ success:false, data:null })),
        api.get('/approvals?status=PENDING&limit=5').then(r=>r.data).catch(()=>({ success:false, data:[] })),
      ]);
      if (exec.success && exec.data)  setData(exec.data);
      if (appr.success)  setApprovals(appr.data||[]);
      setLastRefresh(new Date());
    } catch(e) {
      console.error('Dashboard load error:', e);
    } finally { setLoading(false); }
  }, []);

  // Auto-refresh every 30s (MGMT-FE-001: real-time < 30s)
  useEffect(() => { load(); const t=setInterval(load,60000); return()=>clearInterval(t); }, [load]);

  const getVol = (status) => data?.volumes?.find(v=>v.status===status)?.count || 0;
  const totalToday = (data?.volumes||[]).reduce((s,v)=>s+parseInt(v.count),0);

  const KPI_TARGETS = {
    delivery_success: 98,
    first_attempt:    85,
    damage_rate:      0.1,
  };

  return (
    <AppShell title="Executive Dashboard" actions={
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {lastRefresh && <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
        <button className="btn btn-outline btn-sm" onClick={load}><RefreshCw size={14}/> Refresh</button>
      </div>
    }>
      {loading && !data ? <Spinner /> : data ? (
        <>
          {/* KPI row — MGMT-FE-010 targets visible */}
          <div className="stat-grid" style={{ marginBottom:24 }}>
            <KpiCard label="Total Booked (Today)"  value={fmtNum(totalToday)}            icon="📦" />
            <KpiCard label="Delivered Today"       value={fmtNum(getVol('DELIVERED'))}   icon="✅" color="var(--success)" />
            <KpiCard label="Revenue MTD"           value={fmtPKR(data.revenue?.mtd)}     icon="💰" />
            <KpiCard label="Revenue YTD"           value={fmtPKR(data.revenue?.ytd)}     icon="📈" />
            <KpiCard label="Delivery Success"      value={`${data.deliverySuccessRate||0}%`} icon="🎯"
              color={data.deliverySuccessRate>=98?'var(--success)':data.deliverySuccessRate>=90?'var(--warning)':'var(--danger)'}
              target={`Target: ≥${KPI_TARGETS.delivery_success}%`}
              pass={parseFloat(data.deliverySuccessRate||0) >= KPI_TARGETS.delivery_success} />
            <KpiCard label="COD Pending"           value={fmtPKR(data.cod?.pending)}     icon="💳" color="var(--warning)" />
            <KpiCard label="Open Tickets"          value={data.tickets?.open_count||0}   icon="🎫"
              color={data.tickets?.breached>0?'var(--danger)':'var(--info)'}
              target={data.tickets?.breached>0?`${data.tickets.breached} SLA breached`:undefined} />
            <KpiCard label="Open Exceptions"       value={fmtNum((data.exceptions?.delayed||0)+(data.exceptions?.damaged||0))} icon="⚠️" color="var(--warning)" />
          </div>

          {/* Charts */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">7-Day Shipment Trend</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.trend||[]} margin={{ top:4, right:8, bottom:0, left:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d=>d?.slice(5)}/>
                    <YAxis tick={{ fontSize:11 }}/>
                    <Tooltip formatter={v=>fmtNum(v)}/>
                    <Bar dataKey="total"     name="Total"     fill="#1B2A4A" radius={[3,3,0,0]}/>
                    <Bar dataKey="delivered" name="Delivered" fill="#10B981" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">7-Day Revenue</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.trend||[]} margin={{ top:4, right:8, bottom:0, left:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d=>d?.slice(5)}/>
                    <YAxis tick={{ fontSize:11 }} tickFormatter={v=>`${Math.round(v/1000)}k`}/>
                    <Tooltip formatter={v=>fmtPKR(v)}/>
                    <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2} dot={{ r:3 }}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Bottom row */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20 }}>
            {/* Pipeline */}
            <div className="card">
              <div className="card-header"><span className="card-title">Today's Pipeline</span></div>
              <div className="card-body">
                {['BOOKING_CONFIRMED','PICKED_UP','IN_TRANSIT','AT_HUB','OUT_FOR_DELIVERY','DELIVERY_ATTEMPTED'].map(s=>(
                  <ExRow key={s} label={s.replace(/_/g,' ')} val={getVol(s)} cls="badge-info"/>
                ))}
              </div>
            </div>

            {/* Exceptions */}
            <div className="card">
              <div className="card-header"><span className="card-title">Network Exceptions</span></div>
              <div className="card-body">
                <ExRow label="Delayed"      val={data.exceptions?.delayed}     cls="badge-warning"/>
                <ExRow label="Undelivered"  val={data.exceptions?.undelivered} cls="badge-danger"/>
                <ExRow label="Damaged"      val={data.exceptions?.damaged}     cls="badge-danger"/>
                <ExRow label="Lost"         val={data.exceptions?.lost}        cls="badge-gray"/>
                <ExRow label="Scan Gaps"    val={data.exceptions?.scan_gaps}   cls="badge-warning"/>
              </div>
            </div>

            {/* Pending approvals (MGMT-FE-007) */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Pending Approvals</span>
                <Link to="/approvals" className="btn btn-outline btn-sm" style={{ fontSize:'.72rem' }}>View All</Link>
              </div>
              <div className="card-body">
                {approvals.length ? approvals.map(a=>(
                  <div key={a.workflow_id} style={{ padding:'7px 0', borderBottom:'1px solid var(--gray-100)' }}>
                    <div style={{ fontSize:'.82rem', fontWeight:500 }}>{a.approval_type?.replace(/_/g,' ')}</div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:3 }}>
                      <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{a.requester_name}</span>
                      <span className="badge badge-warning" style={{ fontSize:'.65rem' }}>PENDING</span>
                    </div>
                  </div>
                )) : (
                  <div style={{ textAlign:'center', padding:20, color:'var(--success)' }}>
                    <CheckCircle size={24} style={{ marginBottom:6 }}/>
                    <div style={{ fontSize:'.82rem' }}>No pending approvals</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign:'center', padding:60, color:'var(--gray-400)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📊</div>
          <div style={{ fontWeight:600, marginBottom:8 }}>No dashboard data yet</div>
          <div style={{ fontSize:'.85rem', marginBottom:16 }}>Book your first shipment to see metrics here</div>
          <button className="btn btn-outline btn-sm" onClick={load}>↻ Retry</button>
        </div>
      )}
    </AppShell>
  );
}

function KpiCard({ label, value, icon, color, target, pass }) {
  return (
    <div className="stat-card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div className="stat-label">{label}</div>
        <span style={{ fontSize:'1.3rem' }}>{icon}</span>
      </div>
      <div className="stat-value" style={{ color:color||'var(--navy)', fontSize:'1.4rem' }}>{value}</div>
      {target && (
        <div style={{ fontSize:'.72rem', marginTop:4, color:pass===true?'var(--success)':pass===false?'var(--danger)':'var(--gray-400)' }}>
          {pass===true?'✓ ':pass===false?'✗ ':''}{target}
        </div>
      )}
    </div>
  );
}

function ExRow({ label, val, cls }) {
  return (
    <div className="flex-between" style={{ padding:'7px 0', borderBottom:'1px solid var(--gray-100)' }}>
      <span style={{ fontSize:'.875rem', textTransform:'capitalize' }}>{label.toLowerCase()}</span>
      <span className={`badge ${cls}`}>{val||0}</span>
    </div>
  );
}
